import React, { useMemo, useState } from "react";
import { Save, Pencil, MessageSquare } from "lucide-react";
import { REVENUE_CURRENCIES, OBSERVATION_LEVELS } from "../../config/constants";
import { normalizeCurrencyCode } from "../../utils/formatters";

const toInputValue = (value) =>
  value !== undefined && value !== null && value !== "" ? String(value) : "";

const RevenueAdminPanel = ({
  globalTarget,
  teamProgress,
  onSaveTarget,
  onSaveObjective,
  onAddObservation,
}) => {
  const [editingTarget, setEditingTarget] = useState(false);
  const [targetForm, setTargetForm] = useState(() => ({
    annualTarget: toInputValue(globalTarget?.annualTarget),
    monthlyTarget: toInputValue(globalTarget?.monthlyTarget),
    weeklyTarget: toInputValue(globalTarget?.weeklyTarget),
    dailyTarget: toInputValue(globalTarget?.dailyTarget),
    currency: normalizeCurrencyCode(globalTarget?.currency, "XOF"),
  }));
  const [editingUser, setEditingUser] = useState(null);
  const [objectiveForm, setObjectiveForm] = useState({
    annualTarget: "",
    monthlyTarget: "",
    weeklyTarget: "",
    dailyTarget: "",
    currency: normalizeCurrencyCode(globalTarget?.currency, "XOF"),
  });
  const [observationUser, setObservationUser] = useState(null);
  const [observationForm, setObservationForm] = useState({
    level: "info",
    message: "",
  });
  const [savingTarget, setSavingTarget] = useState(false);
  const [savingObjective, setSavingObjective] = useState(false);
  const [savingObservation, setSavingObservation] = useState(false);

  const currencyFormatter = useMemo(() => {
    const currency = normalizeCurrencyCode(globalTarget?.currency, "XOF");
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
    });
  }, [globalTarget?.currency]);

  const handleTargetChange = (event) => {
    const { name, value } = event.target;
    setTargetForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleTargetSubmit = async (event) => {
    event.preventDefault();
    setSavingTarget(true);
    try {
      await onSaveTarget({
        ...targetForm,
        currency: normalizeCurrencyCode(targetForm.currency, "XOF"),
      });
      setEditingTarget(false);
    } finally {
      setSavingTarget(false);
    }
  };

  const startEditObjective = (entry) => {
    setEditingUser(entry.userId);
    setObjectiveForm({
      annualTarget: toInputValue(entry.objective?.annualTarget),
      monthlyTarget: toInputValue(entry.objective?.monthlyTarget),
      weeklyTarget: toInputValue(entry.objective?.weeklyTarget),
      dailyTarget: toInputValue(entry.objective?.dailyTarget),
      currency: normalizeCurrencyCode(
        entry.objective?.currency || globalTarget?.currency,
        "XOF"
      ),
    });
  };

  const handleObjectiveChange = (event) => {
    const { name, value } = event.target;
    setObjectiveForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleObjectiveSubmit = async (event) => {
    event.preventDefault();
    if (!editingUser) return;
    setSavingObjective(true);
    try {
      await onSaveObjective(editingUser, {
        ...objectiveForm,
        currency: normalizeCurrencyCode(objectiveForm.currency, "XOF"),
      });
      setEditingUser(null);
    } finally {
      setSavingObjective(false);
    }
  };

  const openObservation = (entry) => {
    setObservationUser(entry.userId);
    setObservationForm({
      level: entry.objective?.lastObservationLevel || "info",
      message: "",
    });
  };

  const handleObservationChange = (event) => {
    const { name, value } = event.target;
    setObservationForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleObservationSubmit = async (event) => {
    event.preventDefault();
    if (!observationUser || !observationForm.message.trim()) return;
    setSavingObservation(true);
    try {
      await onAddObservation(observationUser, observationForm);
      setObservationUser(null);
      setObservationForm({ level: "info", message: "" });
    } finally {
      setSavingObservation(false);
    }
  };

  return (
    <section className="panel-surface p-6 space-y-6">
      <header className="flex flex-col gap-2">
        <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
          Paramétrage des objectifs
        </h2>
        <p className="text-sm text-[var(--color-text-secondary)]">
          Ajustez le chiffre d'affaires global et définissez les objectifs par
          commercial.
        </p>
      </header>

      <form
        onSubmit={handleTargetSubmit}
        className="panel-quiet p-4 space-y-4 border border-[var(--color-border-soft)]"
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
              Objectif global
            </h3>
            <p className="text-xs text-[var(--color-text-secondary)]">
              Chiffre d'affaires attendu pour l'équipe
            </p>
          </div>
          {!editingTarget && (
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setEditingTarget(true)}
            >
              <Pencil className="w-4 h-4" /> Modifier
            </button>
          )}
        </div>

        <div className="grid gap-3 md:grid-cols-5">
          {[
            { name: "annualTarget", label: "Annuel" },
            { name: "monthlyTarget", label: "Mensuel" },
            { name: "weeklyTarget", label: "Hebdomadaire" },
            { name: "dailyTarget", label: "Quotidien" },
          ].map(({ name, label }) => (
            <div key={name} className="md:col-span-1">
              <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                {label}
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                name={name}
                value={targetForm[name]}
                onChange={handleTargetChange}
                className="input-field"
                disabled={!editingTarget}
              />
            </div>
          ))}

          <div className="md:col-span-1">
            <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
              Devise
            </label>
            <select
              name="currency"
              value={targetForm.currency}
              onChange={handleTargetChange}
              className="input-field"
              disabled={!editingTarget}
            >
              {REVENUE_CURRENCIES.map((currency) => (
                <option key={currency.code} value={currency.code}>
                  {currency.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {editingTarget && (
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={savingTarget}
            >
              <Save className="w-4 h-4" />{" "}
              {savingTarget ? "Enregistrement..." : "Enregistrer"}
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => {
                setTargetForm({
                  annualTarget: toInputValue(globalTarget?.annualTarget),
                  monthlyTarget: toInputValue(globalTarget?.monthlyTarget),
                  weeklyTarget: toInputValue(globalTarget?.weeklyTarget),
                  dailyTarget: toInputValue(globalTarget?.dailyTarget),
                  currency: normalizeCurrencyCode(
                    globalTarget?.currency,
                    "XOF"
                  ),
                });
                setEditingTarget(false);
              }}
              disabled={savingTarget}
            >
              Annuler
            </button>
          </div>
        )}

        {!editingTarget && (
          <div className="text-xs text-[var(--color-text-muted)]">
            Objectif annuel actuel :
            <strong className="ml-2 text-[var(--color-text-primary)]">
              {currencyFormatter.format(globalTarget?.annualTarget || 0)}
            </strong>
          </div>
        )}
      </form>

      <section className="space-y-4">
        <header className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
            Objectifs par commercial
          </h3>
          <span className="text-xs text-[var(--color-text-muted)]">
            {teamProgress.length} profils suivis
          </span>
        </header>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[var(--color-text-muted)] border-b border-[var(--color-border-soft)]">
                <th className="py-3 px-4">Commercial</th>
                <th className="py-3 px-4">Objectif annuel</th>
                <th className="py-3 px-4">Réalisé</th>
                <th className="py-3 px-4">Progression</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border-soft)]">
              {teamProgress.map((entry) => {
                const name =
                  entry.user?.displayName ||
                  `${entry.user?.firstName || ""} ${
                    entry.user?.lastName || ""
                  }`.trim() ||
                  entry.user?.email ||
                  entry.userId;
                const objectif = Number(entry.objective?.annualTarget) || 0;
                const realise = Number(entry.progress?.total) || 0;
                const percent =
                  objectif > 0
                    ? Math.min(100, Math.round((realise / objectif) * 100))
                    : 0;

                return (
                  <tr
                    key={entry.userId}
                    className="bg-[var(--color-bg-surface)]/40"
                  >
                    <td className="py-3 px-4">
                      <div className="flex flex-col">
                        <span className="font-semibold text-[var(--color-text-primary)]">
                          {name}
                        </span>
                        <span className="text-xs text-[var(--color-text-muted)]">
                          {entry.user?.email || "Sans email"}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {currencyFormatter.format(objectif)}
                    </td>
                    <td className="py-3 px-4">
                      {currencyFormatter.format(realise)}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-32 rounded-full bg-[var(--color-bg-surface)]">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-700"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                        <span className="text-xs font-semibold text-[var(--color-text-secondary)]">
                          {percent}%
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          className="btn btn-outline btn-xs"
                          onClick={() => startEditObjective(entry)}
                        >
                          <Pencil className="w-3 h-3" /> Objectifs
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline btn-xs"
                          onClick={() => openObservation(entry)}
                        >
                          <MessageSquare className="w-3 h-3" /> Observation
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {editingUser && (
        <form
          onSubmit={handleObjectiveSubmit}
          className="panel-quiet p-4 border border-[var(--color-border-soft)] space-y-4"
        >
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
            Ajuster les objectifs
          </h3>
          <div className="grid gap-3 md:grid-cols-5">
            {[
              { name: "annualTarget", label: "Annuel" },
              { name: "monthlyTarget", label: "Mensuel" },
              { name: "weeklyTarget", label: "Hebdomadaire" },
              { name: "dailyTarget", label: "Quotidien" },
            ].map(({ name, label }) => (
              <div key={name} className="md:col-span-1">
                <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                  {label}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  name={name}
                  value={objectiveForm[name]}
                  onChange={handleObjectiveChange}
                  className="input-field"
                />
              </div>
            ))}
            <div className="md:col-span-1">
              <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                Devise
              </label>
              <select
                name="currency"
                value={objectiveForm.currency}
                onChange={handleObjectiveChange}
                className="input-field"
              >
                {REVENUE_CURRENCIES.map((currency) => (
                  <option key={currency.code} value={currency.code}>
                    {currency.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={savingObjective}
            >
              {savingObjective ? "Enregistrement..." : "Enregistrer"}
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setEditingUser(null)}
              disabled={savingObjective}
            >
              Annuler
            </button>
          </div>
        </form>
      )}

      {observationUser && (
        <form
          onSubmit={handleObservationSubmit}
          className="panel-quiet p-4 border border-[var(--color-border-soft)] space-y-4"
        >
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
            Ajouter une observation
          </h3>
          <div className="grid gap-3 md:grid-cols-4">
            <div className="md:col-span-1">
              <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                Niveau
              </label>
              <select
                name="level"
                value={observationForm.level}
                onChange={handleObservationChange}
                className="input-field"
              >
                {OBSERVATION_LEVELS.map((level) => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-semibold text-[var(--color-text-secondary)] mb-1">
                Message
              </label>
              <textarea
                name="message"
                value={observationForm.message}
                onChange={handleObservationChange}
                rows={3}
                className="input-field"
                placeholder="Partagez un encouragement ou un avertissement"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={savingObservation}
            >
              {savingObservation ? "Envoi..." : "Publier"}
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setObservationUser(null)}
              disabled={savingObservation}
            >
              Annuler
            </button>
          </div>
        </form>
      )}
    </section>
  );
};

export default RevenueAdminPanel;
